import { CheckIcon } from '@heroicons/react/24/outline';
import { cn } from '@/utils/cn';

export interface WizardStep {
  label: string;
  hint: string;
}

interface WizardStepperProps {
  steps: WizardStep[];
  current: number;
  maxReached: number;
  onJump?: (index: number) => void;
}

export function WizardStepper({ steps, current, maxReached, onJump }: WizardStepperProps) {
  const reachable = Math.max(maxReached, current);

  return (
    <ol className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
      {steps.map((step, index) => {
        const done = index < current;
        const active = index === current;
        const locked = index > reachable;

        return (
          <li key={step.label} className="flex flex-1 items-center gap-2">
            <button
              type="button"
              disabled={locked || !onJump}
              onClick={() => onJump?.(index)}
              className={cn(
                'group flex flex-1 items-center gap-3 rounded-lg px-3 py-2 text-left transition-colors',
                active && 'bg-indigo-50 ring-1 ring-indigo-300 dark:bg-indigo-900/20 dark:ring-indigo-700',
                !active && !locked && 'hover:bg-gray-100 dark:hover:bg-gray-800',
                locked && 'opacity-50 cursor-not-allowed'
              )}
            >
              <span
                className={cn(
                  'flex h-7 w-7 shrink-0 items-center justify-center rounded-full border text-xs font-semibold transition-colors',
                  active && 'border-indigo-600 bg-indigo-600 text-white dark:border-indigo-500 dark:bg-indigo-500',
                  done && 'border-emerald-500 bg-emerald-500 text-white dark:border-emerald-500 dark:bg-emerald-500',
                  !active && !done && 'border-gray-300 text-gray-600 dark:border-gray-600 dark:text-gray-400'
                )}
              >
                {done ? <CheckIcon className="h-3.5 w-3.5" /> : index + 1}
              </span>
              <span className="min-w-0">
                <span className="block truncate text-sm font-medium text-gray-900 dark:text-white">
                  {step.label}
                </span>
                <span className="block truncate text-xs text-gray-500 dark:text-gray-400">
                  {step.hint}
                </span>
              </span>
            </button>
            {index < steps.length - 1 && (
              <span className="hidden h-px w-4 bg-gray-300 dark:bg-gray-600 sm:block" aria-hidden="true" />
            )}
          </li>
        );
      })}
    </ol>
  );
}
